import React from 'react';
import { SentimentBadge } from '@/components/SentimentBadge';
import type { DiaryNoteItem } from '@/types/diary';
import { formatShortDate, formatTime } from '@/utils/date';
import { linkifyText } from '@/utils/html';
import { BULLET_SYMBOL } from '@/utils/text';

interface Props {
  item: DiaryNoteItem;
  hasDate?: boolean;
  isExpanded?: boolean;
}

export const DiaryNoteCard: React.FC<Props> = ({
  item,
  hasDate = false,
  isExpanded = false,
}) => {
  const formattedDate = isExpanded
    ? `${formatShortDate(item.timeCreated)}, ${formatTime(item.timeCreated)}`
    : formatShortDate(item.timeCreated);

  return (
    <div className="flex w-full flex-col gap-2 rounded-xl bg-card p-4 text-left shadow-card transition-colors group-hover:bg-hover group-active:bg-on-primary-surface contrast:border contrast:border-border">
      <div className="flex h-[22px] w-full items-center justify-between gap-2">
        <span className="text-caption-12 min-w-0 truncate rounded-xl bg-on-primary-surface px-2 py-0.5 text-foreground">
          {item.title}
        </span>
        {(hasDate || isExpanded) && (
          <span className="text-body-13 shrink-0 text-subtle-foreground">
            {formattedDate}
          </span>
        )}
      </div>

      <div className="flex w-full flex-col gap-1">
        <div className="flex w-full items-center gap-3">
          <div className="text-body-14 flex min-w-0 flex-1 items-center gap-1 text-foreground">
            <span className="text-subhead-14 truncate">{item.authorName}</span>
            {!!item.subject && (
              <>
                <span className="shrink-0">{BULLET_SYMBOL}</span>
                <span className="text-subhead-14 truncate">{`(${item.subject})`}</span>
              </>
            )}
          </div>
          {!!item.type && <SentimentBadge type={item.type} />}
        </div>

        <p
          className={`rich-text text-body-13 w-full text-foreground ${isExpanded ? 'whitespace-pre-line' : 'truncate'}`}
          dangerouslySetInnerHTML={{ __html: linkifyText(item.text) }}
        />
      </div>
    </div>
  );
};
